import { normalize } from './recipes';
import { createPantryEntry, getPantryKey, normalizePantryState } from './pantryStorage';
import {
  convertIngredientQuantityAmount,
  convertQuantityAmount,
  getPantryItemQuantity,
  ingredientMatchesPantryItem,
} from './recipePricing';

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(typeof value === 'string' ? value.replace(',', '.') : value);
  return Number.isFinite(number) ? number : null;
};

const getMatchCandidates = (item) => (
  Array.isArray(item?.matchCandidates) && item.matchCandidates.length > 0
    ? item.matchCandidates
    : [{ produkt_navn: item?.name }]
);

const pantryMatchesItem = (item, pantryItem) => getMatchCandidates(item).some(
  (candidate) => ingredientMatchesPantryItem(candidate, pantryItem)
);

const roundPurchaseCount = (remainingAmount, packageSize) => {
  if (remainingAmount <= 0) {
    return 0;
  }
  return Math.ceil((remainingAmount / packageSize) - 1e-9);
};

export function consolidateShoppingItemPurchase(item) {
  const originalPackageCount = toFiniteNumber(item?.packageCount) || 1;
  const packageSize = toFiniteNumber(item?.packageSize);
  const packageUnit = normalize(item?.packageUnit).toLocaleLowerCase('da-DK');
  const requirements = Array.isArray(item?.requirements) ? item.requirements : [];

  if (packageSize === null || packageSize <= 0 || !packageUnit || requirements.length === 0) {
    return {
      ...item,
      packageCount: originalPackageCount,
    };
  }

  const requiredAmounts = requirements.map((requirement) => convertIngredientQuantityAmount(
    requirement?.amount,
    requirement?.unit,
    packageUnit,
    requirement?.ingredient
  ));
  if (requiredAmounts.some((amount) => amount === null || amount < 0)) {
    return {
      ...item,
      packageCount: originalPackageCount,
    };
  }

  const combinedRequiredAmount = requiredAmounts.reduce((sum, amount) => sum + amount, 0);
  const packageCount = Math.max(1, roundPurchaseCount(combinedRequiredAmount, packageSize));
  const weeklyPackagePrice = toFiniteNumber(item?.weeklyPackagePrice);
  const normalPackagePrice = toFiniteNumber(item?.normalPackagePrice);

  return {
    ...item,
    packageCount,
    requiredAmount: combinedRequiredAmount,
    requiredUnit: packageUnit,
    weeklyPriceAmount: weeklyPackagePrice === null
      ? toFiniteNumber(item?.weeklyPriceAmount) || 0
      : weeklyPackagePrice * packageCount,
    normalPriceAmount: normalPackagePrice === null
      ? toFiniteNumber(item?.normalPriceAmount) || 0
      : normalPackagePrice * packageCount,
  };
}

export function summarizeShoppingGroups(groups, mealCount = 0) {
  const values = (Array.isArray(groups) ? groups : []).reduce((summary, group) => ({
    weeklyTotal: summary.weeklyTotal + (toFiniteNumber(group?.weeklyPriceAmount) || 0),
    normalTotal: summary.normalTotal + (toFiniteNumber(group?.normalPriceAmount) || 0),
  }), { weeklyTotal: 0, normalTotal: 0 });

  return {
    ...values,
    savingsTotal: Math.max(0, values.normalTotal - values.weeklyTotal),
    averagePerMeal: mealCount > 0 ? values.weeklyTotal / mealCount : 0,
  };
}

export function applyPantryToShoppingGroups(groups, pantryItems) {
  const pantryPool = (Array.isArray(pantryItems) ? pantryItems : [])
    .map((item) => {
      const quantity = getPantryItemQuantity(item);
      return {
        item,
        remainingAmount: quantity.amount,
        unit: quantity.unit,
      };
    })
    .filter((entry) => entry.remainingAmount !== null && entry.remainingAmount > 0 && entry.unit);

  return (Array.isArray(groups) ? groups : []).map((group) => {
    const items = (Array.isArray(group.items) ? group.items : []).map((item) => {
      const originalPackageCount = toFiniteNumber(item.packageCount) || 1;
      const packageSize = toFiniteNumber(item.packageSize);
      const packageUnit = normalize(item.packageUnit).toLocaleLowerCase('da-DK');

      if (packageSize === null || packageSize <= 0 || !packageUnit) {
        return {
          ...item,
          originalPackageCount,
          originalWeeklyPriceAmount: toFiniteNumber(item.weeklyPriceAmount) || 0,
          originalNormalPriceAmount: toFiniteNumber(item.normalPriceAmount) || 0,
          pantryUsedAmount: 0,
          pantryUsedUnit: packageUnit,
          isFullyCoveredByPantry: false,
        };
      }

      const structuredRequiredAmount = toFiniteNumber(item.requiredAmount);
      const requiredAmount = structuredRequiredAmount !== null
        && normalize(item.requiredUnit).toLocaleLowerCase('da-DK') === packageUnit
        ? structuredRequiredAmount
        : originalPackageCount * packageSize;
      let pantryUsedAmount = 0;

      pantryPool.forEach((poolEntry) => {
        if (pantryUsedAmount >= requiredAmount || !pantryMatchesItem(item, poolEntry.item)) {
          return;
        }

        const availableInPackageUnit = convertQuantityAmount(
          poolEntry.remainingAmount,
          poolEntry.unit,
          packageUnit
        );
        if (availableInPackageUnit === null || availableInPackageUnit <= 0) {
          return;
        }

        const usedInPackageUnit = Math.min(
          availableInPackageUnit,
          requiredAmount - pantryUsedAmount
        );
        const usedInPantryUnit = convertQuantityAmount(
          usedInPackageUnit,
          packageUnit,
          poolEntry.unit
        );
        if (usedInPantryUnit === null) {
          return;
        }

        pantryUsedAmount += usedInPackageUnit;
        poolEntry.remainingAmount = Math.max(0, poolEntry.remainingAmount - usedInPantryUnit);
      });

      const remainingAmount = Math.max(0, requiredAmount - pantryUsedAmount);
      const packageCount = roundPurchaseCount(remainingAmount, packageSize);
      const purchaseRatio = originalPackageCount > 0 ? packageCount / originalPackageCount : 0;
      const weeklyPriceAmount = (toFiniteNumber(item.weeklyPriceAmount) || 0) * purchaseRatio;
      const normalPriceAmount = (toFiniteNumber(item.normalPriceAmount) || 0) * purchaseRatio;

      return {
        ...item,
        packageCount,
        originalPackageCount,
        originalWeeklyPriceAmount: toFiniteNumber(item.weeklyPriceAmount) || 0,
        originalNormalPriceAmount: toFiniteNumber(item.normalPriceAmount) || 0,
        weeklyPriceAmount,
        normalPriceAmount,
        pantryUsedAmount,
        pantryUsedUnit: packageUnit,
        isFullyCoveredByPantry: packageCount === 0,
      };
    });
    const weeklyPriceAmount = items.reduce((sum, item) => sum + (item.weeklyPriceAmount || 0), 0);
    const normalPriceAmount = items.reduce((sum, item) => sum + (item.normalPriceAmount || 0), 0);

    return {
      ...group,
      items,
      weeklyPriceAmount,
      normalPriceAmount,
      savingsAmount: Math.max(0, normalPriceAmount - weeklyPriceAmount),
    };
  });
}

export function getPurchasableShoppingGroups(groups) {
  return (Array.isArray(groups) ? groups : [])
    .map((group) => ({
      ...group,
      items: (Array.isArray(group.items) ? group.items : []).filter(
        (item) => !item.isFullyCoveredByPantry
      ),
    }))
    .filter((group) => group.items.length > 0);
}

export function getMatchingPantryEntry(pantryState, shoppingItem, targetUnit = '') {
  return Object.entries(pantryState || {}).find(([key, entry]) => {
    if (!pantryMatchesItem(shoppingItem, { key, ...entry })) {
      return false;
    }
    if (!targetUnit) {
      return true;
    }

    const quantity = getPantryItemQuantity(entry);
    return convertQuantityAmount(quantity.amount, quantity.unit, targetUnit) !== null;
  }) || null;
}

const formatAmount = (amount) => Number.isInteger(amount)
  ? amount.toString()
  : amount.toLocaleString('da-DK', { maximumFractionDigits: 2 });

const formatPackageLabel = (packageCount, packageSize, packageUnit) => {
  const packageLabel = packageCount === 1 ? '1 pakke' : `${formatAmount(packageCount)} pakker`;
  const sizeLabel = [packageSize, normalize(packageUnit)].filter(Boolean).join(' ');
  return sizeLabel ? `${packageLabel} á ${sizeLabel}` : packageLabel;
};

export function addShoppingItemToPantryState(pantryState, item, options = {}) {
  const pantryKey = getPantryKey(item?.name);
  if (!pantryKey) {
    return { state: pantryState || {}, key: '' };
  }

  const currentState = normalizePantryState(pantryState);
  const packageCount = toFiniteNumber(item?.packageCount);
  const packageSize = toFiniteNumber(item?.packageSize);
  const packageUnit = normalize(item?.packageUnit);
  const purchasedAmount = packageCount !== null && packageSize !== null
    ? packageCount * packageSize
    : null;
  const matchingEntries = Object.entries(currentState).filter(([key, entry]) => {
    if (!pantryMatchesItem(item, { key, ...entry })) {
      return false;
    }
    const quantity = getPantryItemQuantity(entry);
    return convertQuantityAmount(quantity.amount, quantity.unit, packageUnit) !== null;
  });
  const existingEntryRecord = matchingEntries.reduce((newest, candidate) => (
    new Date(candidate[1]?.updatedAt || 0).getTime() >= new Date(newest?.[1]?.updatedAt || 0).getTime()
      ? candidate
      : newest
  ), null);
  const existingEntry = existingEntryRecord?.[1];
  const existingAmountInPurchaseUnit = matchingEntries.reduce((sum, [, entry]) => {
    const quantity = getPantryItemQuantity(entry);
    return sum + (convertQuantityAmount(quantity.amount, quantity.unit, packageUnit) || 0);
  }, 0);
  const canCombineQuantities = purchasedAmount !== null && matchingEntries.length > 0;
  const targetKey = existingEntryRecord?.[0]
    || (currentState[pantryKey] ? `${pantryKey} (${packageUnit || 'køb'})` : pantryKey);
  const combinedQuantityAmount = canCombineQuantities
    ? existingAmountInPurchaseUnit + purchasedAmount
    : purchasedAmount;
  const hasMatchingPackages = canCombineQuantities && matchingEntries.every(([, entry]) => (
    toFiniteNumber(entry?.packageSize) === packageSize
    && normalize(entry?.packageUnit).toLowerCase() === packageUnit.toLowerCase()
  ));
  const combinedPackageCount = hasMatchingPackages
    ? matchingEntries.reduce(
      (sum, [, entry]) => sum + (toFiniteNumber(entry?.packageCount) || 0),
      packageCount
    )
    : packageCount;
  const amountLabel = hasMatchingPackages
    ? formatPackageLabel(combinedPackageCount, packageSize, packageUnit)
    : combinedQuantityAmount !== null
      ? `${formatAmount(combinedQuantityAmount)} ${packageUnit} tilbage`.trim()
      : formatPackageLabel(packageCount || 1, packageSize, packageUnit);

  const nextState = { ...currentState };
  matchingEntries.forEach(([key]) => delete nextState[key]);

  return {
    key: targetKey,
    state: {
      ...nextState,
      [targetKey]: createPantryEntry({
        productName: item.name,
        amountLabel,
        source: 'shopping',
        category: item.category || existingEntry?.category,
        freshnessKind: existingEntry?.freshnessKind,
        quantityAmount: combinedQuantityAmount,
        quantityUnit: packageUnit,
        packageCount: hasMatchingPackages ? combinedPackageCount : null,
        packageSize: hasMatchingPackages ? packageSize : null,
        packageUnit,
        note: normalize(options.note),
        trackingMode: options.trackingMode,
        confidence: options.trackingMode === 'smart' ? 'confirmed' : '',
      }),
    },
  };
}
